import { Eye, Pencil, /* Power, */ Mail, Phone, MapPin, CreditCard, CheckCircle, Clock, User } from "lucide-react";

const getStatusBadge = (client) => {
  const status = client.status?.toLowerCase() || "inactive";
  if (status === "active") {
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
        <CheckCircle className="mr-1 w-3 h-3" />
        Active
      </span>
    );
  } else if (status === "pending") {
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full">
        <Clock className="mr-1 w-3 h-3" />
        Pending
      </span>
    );
  } else {
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full">
        <Clock className="mr-1 w-3 h-3" />
        Inactive
      </span>
    );
  }
};

const ClientTable = ({ clients, onView, onEdit /* onActivate */ }) => {
  if (clients.length === 0) return null;

  return (
    <div className="hidden overflow-x-auto lg:block">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
              Client Details
            </th>
            <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
              Contact Info
            </th>
            <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
              Location
            </th>
            <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
              ID Document
            </th>
            <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
              Status
            </th>
            <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
              Agent Information
            </th>
            <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase whitespace-nowrap">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {clients.map((client) => (
            <tr
              key={client.slug || client.id}
              className="transition-colors duration-150 hover:bg-gray-50"
            >
              <td className="px-6 py-4">
                <div className="flex items-center">
                  <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full shadow-sm">
                    <span className="text-sm font-medium text-white">
                      {client.firstName?.charAt(0) || "C"}
                      {client.lastName?.charAt(0) || "L"}
                    </span>
                  </div>
                  <div className="ml-4 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">
                      {client.firstName} {client.lastName}
                    </div>
                    {client.cardNumber && (
                      <div className="text-sm text-gray-500 truncate">
                        ID: {client.cardNumber}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="space-y-1">
                  {client.email ? (
                    <div className="flex items-center text-sm text-gray-900 truncate">
                      <Mail className="flex-shrink-0 mr-2 w-3 h-3 text-gray-400" />
                      <span className="truncate">{client.email}</span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Mail className="flex-shrink-0 mr-2 w-3 h-3 text-gray-400" />
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                        Not Available
                      </span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-500 truncate">
                    <Phone className="flex-shrink-0 mr-2 w-3 h-3 text-gray-400" />
                    <span className="truncate">{client.phone || "N/A"}</span>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center min-w-0">
                  <MapPin className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                  <div className="min-w-0">
                    {client.location ? (
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {client.location}
                      </div>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                        Not Available
                      </span>
                    )}
                    {client.region && (
                      <div className="text-sm text-gray-500 truncate">
                        {client.region}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {client.documentType || "N/A"}
                  </div>
                  {client.issueDate && (
                    <div className="text-xs text-gray-500">
                      Issued: {new Date(client.issueDate).toLocaleDateString()}
                    </div>
                  )}
                  {client.expiryDate && (
                    <div className="text-xs text-gray-500">
                      Expires: {new Date(client.expiryDate).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 align-top">{getStatusBadge(client)}</td>
              <td className="px-6 py-4">
                {client.agent ? (
                  <div className="space-y-1">
                    <div className="flex items-center">
                      <User className="flex-shrink-0 mr-2 w-3 h-3 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {client.agentName || "N/A"}
                      </span>
                    </div>
                    {client.agentCode && (
                      <span className="inline-block px-2 py-0.5 font-mono text-xs font-semibold text-emerald-800 bg-emerald-100 rounded">
                        {client.agentCode}
                      </span>
                    )}
                    {client.agentEmail && (
                      <div className="text-xs text-gray-500 truncate">
                        {client.agentEmail}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">No Agent</span>
                )}
              </td>
              <td className="px-6 py-4 text-right">
                <div className="flex justify-end items-center space-x-1">
                  <button
                    onClick={() => onView(client)}
                    className="p-2 text-gray-400 rounded-lg transition-colors hover:text-emerald-600 hover:bg-emerald-50 touch-manipulation"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onEdit(client)}
                    className="p-2 text-gray-400 rounded-lg transition-colors hover:text-blue-600 hover:bg-blue-50 touch-manipulation"
                    title="Edit Client"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {/* Activation button - COMMENTED OUT */}
                  {/* <button
                    onClick={() => onActivate(client)}
                    className={`p-2 rounded-lg transition-colors touch-manipulation ${
                      client.status?.toLowerCase() === "active"
                        ? "text-red-500 hover:text-red-600 hover:bg-red-50"
                        : "text-gray-400 hover:text-green-600 hover:bg-green-50"
                    }`}
                    title={
                      client.status?.toLowerCase() === "active"
                        ? "Deactivate Client"
                        : "Activate Client"
                    }
                  >
                    <Power className="w-4 h-4" />
                  </button> */}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ClientTable;
